import { useEffect, useState } from "react";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import "./Contact.css";

const INITIAL = {
  firstName: "",
  lastName: "",
  email: "",
  message: "",
};

const CONTACT_API_URL = process.env.REACT_APP_CONTACT_API_URL;

function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Contact Us — The Way 5:23";
    return () => {
      document.title =
        "The Way 5:23 — Steward Your Spirit, Soul, and Body";
    };
  }, []);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
    setSubmitError("");
  };

  const validate = () => {
    const next = {};
    if (!form.firstName.trim()) next.firstName = "First name is required.";
    if (!form.lastName.trim()) next.lastName = "Last name is required.";
    if (!form.email.trim()) {
      next.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      next.email = "Enter a valid email address.";
    }
    if (!form.message.trim()) next.message = "Please enter a message.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    if (!CONTACT_API_URL) {
      setSubmitError(
        "Contact form is not configured yet. Please email us at info@theway523.com."
      );
      return;
    }

    setSubmitting(true);
    setSubmitError("");

    const payload = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      message: form.message.trim(),
    };

    try {
      const response = await fetch(CONTACT_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      let data = null;
      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        data = await response.json();
      }

      if (!response.ok) {
        const message =
          data?.message ||
          data?.error ||
          "Something went wrong. Please try again or email info@theway523.com.";
        throw new Error(message);
      }

      setSubmitted(true);
      setForm(INITIAL);
    } catch (err) {
      setSubmitError(
        err.message ||
          "Unable to send your message. Please try again or email info@theway523.com."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page">
      <SiteHeader />

      <main className="contact">
        <div className="container contact-inner">
          {submitted ? (
            <div className="contact-success" role="status">
              <p className="overline">Thank You</p>
              <h1 className="contact-title">Your message has been received.</h1>
              <p className="contact-lead">
                We appreciate you reaching out. Our team will review your note
                and respond within one business day.
              </p>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setSubmitted(false)}
              >
                Send Another Message
              </button>
            </div>
          ) : (
            <>
              <h1 className="contact-title">Send Us a Message</h1>
              <p className="contact-lead">
                Have a question about our services or need to contact us? Our
                dedicated team is ready to assist you. Please fill out the form
                below and we will respond within one business day.
              </p>

              <form className="contact-form" onSubmit={onSubmit} noValidate>
                <fieldset className="contact-fieldset">
                  <legend className="contact-label">
                    Name <span aria-hidden="true">*</span>
                  </legend>
                  <div className="contact-name-row">
                    <div className="contact-field">
                      <input
                        id="firstName"
                        name="firstName"
                        type="text"
                        autoComplete="given-name"
                        value={form.firstName}
                        onChange={onChange}
                        aria-invalid={Boolean(errors.firstName)}
                        aria-describedby={
                          errors.firstName
                            ? "firstName-error"
                            : "firstName-hint"
                        }
                        disabled={submitting}
                        required
                      />
                      <label
                        htmlFor="firstName"
                        id="firstName-hint"
                        className="contact-sublabel"
                      >
                        First
                      </label>
                      {errors.firstName && (
                        <p
                          className="contact-error"
                          id="firstName-error"
                          role="alert"
                        >
                          {errors.firstName}
                        </p>
                      )}
                    </div>
                    <div className="contact-field">
                      <input
                        id="lastName"
                        name="lastName"
                        type="text"
                        autoComplete="family-name"
                        value={form.lastName}
                        onChange={onChange}
                        aria-invalid={Boolean(errors.lastName)}
                        aria-describedby={
                          errors.lastName ? "lastName-error" : "lastName-hint"
                        }
                        disabled={submitting}
                        required
                      />
                      <label
                        htmlFor="lastName"
                        id="lastName-hint"
                        className="contact-sublabel"
                      >
                        Last
                      </label>
                      {errors.lastName && (
                        <p
                          className="contact-error"
                          id="lastName-error"
                          role="alert"
                        >
                          {errors.lastName}
                        </p>
                      )}
                    </div>
                  </div>
                </fieldset>

                <div className="contact-field">
                  <label htmlFor="email" className="contact-label">
                    Email <span aria-hidden="true">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={form.email}
                    onChange={onChange}
                    aria-invalid={Boolean(errors.email)}
                    aria-describedby={errors.email ? "email-error" : undefined}
                    disabled={submitting}
                    required
                  />
                  {errors.email && (
                    <p className="contact-error" id="email-error" role="alert">
                      {errors.email}
                    </p>
                  )}
                </div>

                <div className="contact-field">
                  <label htmlFor="message" className="contact-label">
                    Comment or Message <span aria-hidden="true">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={7}
                    value={form.message}
                    onChange={onChange}
                    aria-invalid={Boolean(errors.message)}
                    aria-describedby={
                      errors.message ? "message-error" : undefined
                    }
                    disabled={submitting}
                    required
                  />
                  {errors.message && (
                    <p
                      className="contact-error"
                      id="message-error"
                      role="alert"
                    >
                      {errors.message}
                    </p>
                  )}
                </div>

                {submitError && (
                  <p className="contact-submit-error" role="alert">
                    {submitError}
                  </p>
                )}

                <button
                  type="submit"
                  className="btn btn-primary contact-submit"
                  disabled={submitting}
                >
                  {submitting ? "Sending…" : "Submit"}
                </button>
              </form>
            </>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

export default Contact;
